import { useState, useEffect } from 'react';
import type { Product, ProductVariation, VariationImage } from '@jigflow/shared';
import { useProducts } from '../../contexts/ProductContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { uploadProductImage } from '../../services/productService';
import { Trash2, Plus, Package, Image as ImageIcon } from 'lucide-react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    rectSortingStrategy,
    useSortable,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable image thumbnail component
interface SortableImageThumbnailProps {
    id: string;
    image: string;
    isPrimary: boolean;
    onRemove: () => void;
}

function SortableImageThumbnail({ id, image, isPrimary, onRemove }: SortableImageThumbnailProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`relative group aspect-square rounded overflow-hidden cursor-grab active:cursor-grabbing ${
                isPrimary
                    ? 'ring-2 ring-blue-500'
                    : 'border border-slate-200 dark:border-slate-600'
            } ${isDragging ? 'opacity-50 z-50' : ''}`}
            {...attributes}
            {...listeners}
        >
            <img src={image} alt="" className="w-full h-full object-cover" />

            {/* Primary badge - compact */}
            {isPrimary && (
                <div className="absolute top-0.5 left-0.5 px-1 py-0.5 bg-blue-600 text-white text-[10px] font-medium rounded">
                    P
                </div>
            )}

            {/* Delete button on hover - compact */}
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onRemove();
                }}
                onPointerDown={(e) => e.stopPropagation()}
                className="absolute bottom-0.5 right-0.5 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition hover:bg-red-600"
            >
                <Trash2 size={12} />
            </button>
        </div>
    );
}

interface GeneralTabProps {
    product: Product;
    onChange: (updates: Partial<Product>) => void;
}

export default function GeneralTab({ product, onChange }: GeneralTabProps) {
    const {
        categories,
        getVariationsByProductId,
        getVariationImages,
    } = useProducts();

    const { workspace } = useWorkspace();
    const workspaceId = workspace?.id;

    // Variations state
    const [variations, setVariations] = useState<ProductVariation[]>([]);
    const [selectedVariationId, setSelectedVariationId] = useState<string | null>(null);
    const [variationImages, setVariationImages] = useState<VariationImage[]>([]);
    const [isUploading, setIsUploading] = useState(false);

    // @dnd-kit sensors for drag-to-reorder images
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // 8px movement required before drag starts
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Build unified images array (first = primary)
    const allImages = (() => {
        const images: string[] = [];
        if (product.image) {
            images.push(product.image);
        }
        // Add remaining images that aren't the primary
        (product.images || []).forEach(img => {
            if (img !== product.image && !images.includes(img)) {
                images.push(img);
            }
        });
        return images;
    })();

    // Handle drag end - reorder images
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = allImages.findIndex((_, i) => `image-${i}` === active.id);
        const newIndex = allImages.findIndex((_, i) => `image-${i}` === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
            const reordered = arrayMove(allImages, oldIndex, newIndex);
            onChange({
                image: reordered[0], // First image is primary
                images: reordered
            });
        }
    };

    // Load variations for this product
    useEffect(() => {
        const loadVariations = async () => {
            if (product.id) {
                const vars = await getVariationsByProductId(product.id);
                setVariations(vars);
            }
        };
        loadVariations();
    }, [product.id, getVariationsByProductId]);

    // Load variation images when a variation is selected
    useEffect(() => {
        const loadVariationImages = async () => {
            if (selectedVariationId) {
                const images = await getVariationImages(selectedVariationId);
                setVariationImages(images);
            } else {
                setVariationImages([]);
            }
        };
        loadVariationImages();
    }, [selectedVariationId, getVariationImages]);

    // Handle product-level image upload (when "All Variations" is selected)
    const handleProductImageUpload = async (files: FileList | null) => {
        if (!files || !workspaceId) return;

        setIsUploading(true);
        try {
            const uploadPromises = Array.from(files).map(file =>
                uploadProductImage(workspaceId, product.id, file)
            );

            const uploadedUrls = await Promise.all(uploadPromises);
            const newImages = [...allImages, ...uploadedUrls];

            onChange({
                images: newImages,
                // Set first uploaded as primary if no primary image exists
                image: newImages[0]
            });
        } catch (error) {
            console.error('[GeneralTab] Failed to upload images:', error);
            alert('Failed to upload images. Please try again.');
        } finally {
            setIsUploading(false);
        }
    };

    // Remove an image from the array
    const handleRemoveImage = (imageUrl: string) => {
        const newImages = allImages.filter(img => img !== imageUrl);
        onChange({
            images: newImages,
            // First remaining image becomes primary
            image: newImages[0] || ''
        });
    };

    const hasVariations = variations.length > 0;
    const isEditingVariation = selectedVariationId !== null;

    // Placeholder image URL
    const placeholderImage = 'https://placehold.co/100x100?text=?';

    return (
        <div className="h-full overflow-y-auto p-4">
            <div className="max-w-2xl mx-auto">
                {/* Single unified card */}
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                    {/* Basic Info Header */}
                    <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                        <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2 text-sm">
                            <Package size={16} className="text-blue-600 dark:text-blue-400" />
                            Basic Information
                        </h3>
                    </div>

                    {/* Compact form */}
                    <div className="p-3 space-y-2">
                        {/* Row 1: Name + Category (no price in SaaS) */}
                        <div className="flex gap-3">
                            <div className="flex-1">
                                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                                    Name *
                                </label>
                                <input
                                    type="text"
                                    value={product.name}
                                    onChange={e => onChange({ name: e.target.value })}
                                    placeholder="Product name"
                                    className="w-full px-2 py-1 text-sm border border-slate-200 dark:border-slate-600 rounded bg-slate-50 dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition"
                                />
                            </div>
                            <div className="w-36">
                                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                                    Category
                                </label>
                                <select
                                    value={product.category}
                                    onChange={e => onChange({ category: e.target.value })}
                                    className="w-full px-2 py-1 text-sm border border-slate-200 dark:border-slate-600 rounded bg-slate-50 dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition"
                                >
                                    <option value="">Select</option>
                                    {categories.map(c => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Row 2: Description */}
                        <div>
                            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                                Description
                            </label>
                            <textarea
                                value={product.description}
                                onChange={e => onChange({ description: e.target.value })}
                                placeholder="Optional description..."
                                rows={3}
                                className="w-full px-2 py-1 text-sm border border-slate-200 dark:border-slate-600 rounded bg-slate-50 dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition resize-none"
                            />
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-slate-200 dark:border-slate-700" />

                    {/* Images Header */}
                    <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-between">
                        <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2 text-sm">
                            <ImageIcon size={16} className="text-green-600 dark:text-green-400" />
                            Images
                        </h3>
                        {/* Variation Selector - compact inline */}
                        {hasVariations && (
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">For:</span>
                                <select
                                    value={selectedVariationId || ''}
                                    onChange={e => setSelectedVariationId(e.target.value || null)}
                                    className="px-2 py-0.5 text-xs border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                >
                                    <option value="">All Variations</option>
                                    {variations.map(v => (
                                        <option key={v.id} value={v.id}>{v.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Info text for variation mode - compact */}
                    {isEditingVariation && (
                        <div className="px-3 py-1.5 text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800">
                            Variation image management coming soon. Currently showing "{variations.find(v => v.id === selectedVariationId)?.name}" images (read-only).
                        </div>
                    )}

                    {/* Product-level images (when no variation selected) */}
                    {!isEditingVariation && (
                        <div className="p-3">
                            {allImages.length > 0 ? (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={allImages.map((_, i) => `image-${i}`)}
                                        strategy={rectSortingStrategy}
                                    >
                                        <div className="grid grid-cols-6 gap-2">
                                            {allImages.map((img, idx) => (
                                                <SortableImageThumbnail
                                                    key={`image-${idx}`}
                                                    id={`image-${idx}`}
                                                    image={img}
                                                    isPrimary={idx === 0}
                                                    onRemove={() => handleRemoveImage(img)}
                                                />
                                            ))}

                                            {/* Add Image Button */}
                                            <label className={`flex flex-col items-center justify-center aspect-square border-2 border-dashed border-slate-300 dark:border-slate-600 rounded cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                                {isUploading ? (
                                                    <div className="text-xs text-slate-400">Uploading...</div>
                                                ) : (
                                                    <Plus size={18} className="text-slate-400" />
                                                )}
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    multiple
                                                    disabled={isUploading}
                                                    className="hidden"
                                                    onChange={e => handleProductImageUpload(e.target.files)}
                                                />
                                            </label>
                                        </div>
                                    </SortableContext>
                                </DndContext>
                            ) : (
                                <div className="grid grid-cols-6 gap-2">
                                    {/* Placeholder when no images */}
                                    <div className="relative aspect-square bg-slate-100 dark:bg-slate-700 rounded overflow-hidden border border-slate-200 dark:border-slate-600">
                                        <img src={placeholderImage} alt="Placeholder" className="w-full h-full object-cover" />
                                    </div>

                                    {/* Add Image Button */}
                                    <label className={`flex flex-col items-center justify-center aspect-square border-2 border-dashed border-slate-300 dark:border-slate-600 rounded cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                        {isUploading ? (
                                            <div className="text-xs text-slate-400">Uploading...</div>
                                        ) : (
                                            <Plus size={18} className="text-slate-400" />
                                        )}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            disabled={isUploading}
                                            className="hidden"
                                            onChange={e => handleProductImageUpload(e.target.files)}
                                        />
                                    </label>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Variation-specific images (when a variation is selected) */}
                    {isEditingVariation && (
                        <div className="p-3">
                            <div className="grid grid-cols-6 gap-2">
                                {variationImages.length > 0 ? (
                                    <>
                                        {variationImages.map((img, idx) => (
                                            <div
                                                key={img.id}
                                                className={`relative group aspect-square bg-slate-100 dark:bg-slate-700 rounded overflow-hidden ${
                                                    img.isPrimary ? 'ring-2 ring-blue-500' : 'border border-slate-200 dark:border-slate-600'
                                                }`}
                                            >
                                                <img src={img.image} alt={`Variation image ${idx + 1}`} className="w-full h-full object-cover" />
                                                {img.isPrimary && (
                                                    <div className="absolute top-0.5 left-0.5 px-1 py-0.5 bg-blue-600 text-white text-[10px] font-medium rounded">
                                                        P
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </>
                                ) : (
                                    <div className="col-span-6 flex items-center justify-center py-4 bg-slate-50 dark:bg-slate-700/50 rounded border border-dashed border-slate-300 dark:border-slate-600">
                                        <p className="text-xs text-slate-400">No images. Defaults will be used.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
